import React, {useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {AspectRatio, Box, HStack, IconButton, useMultiStyleConfig} from '@chakra-ui/react'
import Link from '@salesforce/retail-react-app/app/components/link'

import {FavouriteFilledIcon, FavouriteIcon} from '../../icons'
import {InformativeLabelGroup} from '../../informative-label'
import {useWishList} from '../../../hooks/use-wish-list'
import {useWishlistActions} from '../../../hooks/use-wishlist-actions'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {item_gtm_event} from '../../../custom-api/layers/default'
import {getPriceData} from '../../../utils/product-utils'
import {getImageForProductTile} from '../../../utils/utils'
import {useBooleanCustomPreference} from '../../../hooks/use-custom-preference'
import DynamicImage from '../../dynamic-image'

const ProductTileImage = ({
    dynamicImageProps = {
        widths: ['50vw', '50vw', '20vw', '20vw', '25vw'],
    },
    enableFavourite,
    product,
    productUrl,
    compactView,
    priceData,
    variant,
    customImage,
    selectItemTileEventGtm,
    isOfficina = false,
    isCorporateSale = false,
    corporateSaleLabelName = '',
    isFromHelpMeChoose = false,
    productIndex = undefined,
    isDragging = false,
    height = 'auto',
    imageStyle = {},
    isFromProductList = false,
    isInformativeLabelActive = true,
    bundleFirstElement = false,
    isFetchPriority = false,
}) => {
    const styles = useMultiStyleConfig('ProductTile', {
        variant: variant || (compactView ? 'compact' : 'default'),
    })
    const isFavouriteLoading = useRef(false)
    const {data: wishlist} = useWishList()
    const {toggleWishlistItem, isProductInWishlist} = useWishlistActions(wishlist)
    const isFavourite = isProductInWishlist(product?.c_subModelIdentifier ?? product?.productId)
    const hideAccountPref = useBooleanCustomPreference('hideAccount')
    const hideAccount = hideAccountPref !== null ? hideAccountPref : false
    const {formatMessage} = useIntl()
    const {addLayer} = useGoogleTagManager()

    const localizedProductName =
        product?.c_variantIdentifier || product?.name || product?.productName
    const image = getImageForProductTile(product, customImage)

    const handleWishlistClick = async () => {
        if ((isFromHelpMeChoose || isFromProductList) && !isFavourite) {
            await addLayer(
                item_gtm_event.bind(this, {
                    event: 'add_to_wishlist',
                    currency: product?.currency,
                    items: [
                        {
                            item_name: product?.c_defaultName,
                            item_id: product?.productId || product?.id,
                            item_category: product?.c_defaultCategoryName,
                            item_variant: product?.c_variants?.[0]?.productId,
                            price: parseFloat(
                                (
                                    product?.price /
                                    (1 + parseFloat(product?.c_taxRate) / 100)
                                ).toFixed(2),
                            ),
                            fullprice: getPriceData(product)?.isOnSale ? 'reduced' : 'fullprice',
                            rating_points: '',
                            erp_category: product?.c_materialGroup,
                            prdha_category: product?.c_prdhaCategory || product?.c_prdha_category,
                            index: productIndex + 1,
                            stock_status:
                                product?.inventory?.stockLevel > 0 ? 'in-stock' : 'out-of-stock', //?What to do here
                        },
                    ],
                }),
            )
        }
        toggleWishlistItem(product, isFavouriteLoading)
    }

    const productImageAltText = [
        product?.c_categoryName || product?.c_defaultCategoryName,
        product?.productName || product?.c_defaultName,
        product?.representedProduct?.id,
    ]
        .filter(Boolean)
        .join(', ')

    return (
        <Box {...styles.imageWrapper}>
            <AspectRatio {...styles.image} {...(bundleFirstElement ? styles.firstBundleImage : {})}>
                {productUrl && !isDragging ? (
                    <ProductImageLink
                        productUrl={productUrl}
                        isOfficina={isOfficina}
                        selectItemTileEventGtm={selectItemTileEventGtm}
                        productName={localizedProductName}
                    >
                        <>
                            <DynamicImage
                                data-testid="product-tile-image"
                                src={`${image}[?sw={width}&q=60]`}
                                imageProps={{
                                    height: height,
                                    alt: productImageAltText,
                                    loading: 'lazy',
                                    ...dynamicImageProps?.imageProps,
                                }}
                                isFetchPriority={isFetchPriority}
                                {...imageStyle}
                            />
                            <Box {...styles.imageOverlay} />
                        </>
                    </ProductImageLink>
                ) : (
                    <>
                        {image && (
                            <DynamicImage
                                data-testid="product-tile-image"
                                src={`${image}[?sw={width}&q=60]`}
                                // widths={dynamicImageProps?.widths} // TODO: readd this prop when we have a solution for dynamic image widths
                                imageProps={{
                                    // treat img as a decorative item, we don't need to pass `image.alt`
                                    // since it is the same as product name
                                    // which can cause confusion for individuals who uses screen readers
                                    height: height,
                                    alt: '',
                                    loading: 'lazy',
                                    ...dynamicImageProps?.imageProps,
                                }}
                                isFetchPriority={isFetchPriority}
                                {...imageStyle}
                            />
                        )}
                        <Box {...styles.imageOverlay} />
                    </>
                )}
            </AspectRatio>

            <HStack {...styles.badgeGroup}>
                {isInformativeLabelActive && (
                    <InformativeLabelGroup
                        product={product?.representedProduct ?? product}
                        priceData={priceData}
                        isCorporateSale={isCorporateSale}
                        corporateSaleLabelName={corporateSaleLabelName}
                    />
                )}
            </HStack>

            {enableFavourite && (
                <Box
                    onClick={(e) => {
                        // stop click event from bubbling
                        // to avoid user from clicking the underlying
                        // product while the favourite icon is disabled
                        e.preventDefault()
                    }}
                >
                    {!hideAccount && (
                        <IconButton
                            data-testid="wishlist-button"
                            aria-label={
                                isFavourite
                                    ? formatMessage(
                                          {
                                              id: 'product_tile.assistive_msg.remove_from_wishlist',
                                              defaultMessage: 'Remove {product} from wishlist',
                                          },
                                          {product: localizedProductName},
                                      )
                                    : formatMessage(
                                          {
                                              id: 'product_tile.assistive_msg.add_to_wishlist',
                                              defaultMessage: 'Add {product} to wishlist',
                                          },
                                          {product: localizedProductName},
                                      )
                            }
                            icon={
                                isFavourite ? (
                                    <FavouriteFilledIcon
                                        {...styles.favSvgSolid}
                                        data-product-code={product?.representedProduct?.id}
                                    />
                                ) : (
                                    <FavouriteIcon
                                        {...styles.favSvg}
                                        data-product-code={product?.representedProduct?.id}
                                    />
                                )
                            }
                            {...styles.favIcon}
                            onClick={handleWishlistClick}
                            className="js-wishlist-add"
                            data-product-code={product?.representedProduct?.id}
                        />
                    )}
                </Box>
            )}
        </Box>
    )
}

ProductTileImage.propTypes = {
    badgeDetails: PropTypes.array,
    dynamicImageProps: PropTypes.object,
    enableFavourite: PropTypes.bool,
    isFavourite: PropTypes.bool,
    onFavouriteToggle: PropTypes.func,
    imageViewType: PropTypes.string,
    product: PropTypes.object,
    productUrl: PropTypes.string,
    selectableAttributeId: PropTypes.string,
    compactView: PropTypes.bool,
    priceData: PropTypes.object,
    variant: PropTypes.string,
    customImage: PropTypes.string,
    isOfficina: PropTypes.bool,
    isCorporateSale: PropTypes.bool,
    corporateSaleLabelName: PropTypes.string,
    selectItemTileEventGtm: PropTypes.func,
    isFromHelpMeChoose: PropTypes.bool,
    productIndex: PropTypes.number,
    isDragging: PropTypes.bool,
    height: PropTypes.string,
    imageStyle: PropTypes.object,
    isFromProductList: PropTypes.bool,
    isInformativeLabelActive: PropTypes.bool,
    bundleFirstElement: PropTypes.bool,
    isFetchPriority: PropTypes.bool,
}

export default ProductTileImage

const ProductImageLink = ({
    productUrl,
    isOfficina,
    selectItemTileEventGtm,
    productName,
    children,
}) => {
    const {locale} = useMultiSite()
    const intl = useIntl()
    const localeId = locale?.alias

    const ariaLabel = intl.formatMessage(
        {
            id: 'product_tile.view_product.aria_label',
            defaultMessage: 'View {productName}',
        },
        {
            productName,
        },
    )

    return isOfficina ? (
        <a
            href={`/${localeId}${productUrl}`}
            aria-label={ariaLabel}
            onClick={selectItemTileEventGtm}
        >
            {children}
        </a>
    ) : (
        <Link to={productUrl} aria-label={ariaLabel} onClick={selectItemTileEventGtm}>
            {children}
        </Link>
    )
}

ProductImageLink.propTypes = {
    productUrl: PropTypes.string,
    children: PropTypes.node,
    isOfficina: PropTypes.bool,
    selectItemTileEventGtm: PropTypes.func,
    productName: PropTypes.string,
}
